import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  CircularProgress,
  Typography
} from '@mui/material';

import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import useSelectedTenant from '../../../../../components/hooks/useSelectedTenant';
import SummaryTiles, { TILE_ACCENT } from '../../../../../components/utils/SummaryTiles';
import TopBar from '../../../../../components/utils/TopBar';

import { GetServiceFeesBIZcore } from '../../../../../redux/actions/business/core/serviceFeesActions';
import { GetInternationalServiceFeesBIZcore } from '../../../../../redux/actions/business/core/internationalServiceFeesActions';
import { GetInternationalUSDtoUSDServiceFeesBIZcore } from '../../../../../redux/actions/business/core/internationalUSDtoUSDServiceFeesActions';
import { GetBankAccountTypesBIZcore } from '../../../../../redux/actions/business/core/bankAccountTypesActions';
import { GetBanksBIZcore } from '../../../../../redux/actions/business/core/banksAction';
import { GetHolidaysBIZcore } from '../../../../../redux/actions/business/core/holidaysActions';
import { GetTenantBankAccountsBIZcore } from '../../../../../redux/actions/business/core/tenantBankAccountsActions';
import { GetPaymentFrequenciesBIZcore } from '../../../../../redux/actions/business/core/paymentFrequenciesActions';
import { GetServicesBIZsubs } from '../../../../../redux/actions/business/subscriptions/servicesActions';

const LABEL_COLOR = 'rgba(226, 242, 249, 0.6)';
const UNBOUNDED = -1;

const Section = ({ title, hint, children }) => (
  <Box mb={"22px"}>
    <Typography variant='subtitle2' sx={{ mb: hint ? "2px" : "10px" }}>{title}</Typography>
    {
      hint
      &&
      <Typography variant='caption' sx={{ color: LABEL_COLOR, display: "block", mb: "10px" }}>
        {hint}
      </Typography>
    }
    {children}
  </Box>
);

// Same laddering rule the Fee Ranges screen uses: a ladder is one
// (service id, fee name) pair, and bands are min-inclusive / max-exclusive.
const countLadderIssues = (rows) => {
  const lanes = {};
  rows.forEach((r) => {
    const key = `${String(r.ref_service_id ?? "")}::${r.fee_name || ""}`;
    (lanes[key] = lanes[key] || []).push(r);
  });
  let bad = 0;
  Object.values(lanes).forEach((bands) => {
    const sorted = [...bands].sort((a, b) => Number(a.min_trx_amount) - Number(b.min_trx_amount));
    let broken = false;
    if (sorted.length && Number(sorted[0].min_trx_amount) > 0) broken = true;
    for (let i = 1; i < sorted.length; i++) {
      const prevMax = Number(sorted[i - 1].max_trx_amount);
      const curMin = Number(sorted[i].min_trx_amount);
      if (prevMax === UNBOUNDED || curMin !== prevMax) broken = true;
    }
    const last = sorted[sorted.length - 1];
    if (last && Number(last.max_trx_amount) !== UNBOUNDED) broken = true;
    if (broken) bad += 1;
  });
  return { lanes: Object.keys(lanes).length, bad };
};

const HomePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tenantId = useSelectedTenant();

  const coreState = useSelector(state => state["business.core"]);
  const subsState = useSelector(state => state["business.subscriptions"]);
  const selectedTenant = useSelector(state => state["business.core"]?.selectedTenant);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    handleGetAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  async function handleGetAll() {
    setLoading(true);
    const noop = () => {};
    await Promise.all([
      dispatch(GetServiceFeesBIZcore(tenantId, 1, 1000, "", noop)),
      dispatch(GetInternationalServiceFeesBIZcore(tenantId, 1, 1000, "", noop)),
      dispatch(GetInternationalUSDtoUSDServiceFeesBIZcore(tenantId, 1, 1000, "", noop)),
      dispatch(GetBankAccountTypesBIZcore(tenantId, 1, 1000, "", noop)),
      dispatch(GetBanksBIZcore(tenantId, 1, 1000, "", noop)),
      dispatch(GetHolidaysBIZcore(tenantId, 1, 1000, "", noop)),
      dispatch(GetTenantBankAccountsBIZcore(tenantId, 1, 1000, "", noop)),
      dispatch(GetPaymentFrequenciesBIZcore(tenantId, 1, 1000, "", noop)),
      dispatch(GetServicesBIZsubs(1, 1000, "", noop)),
    ]);
    setLoading(false);
  }

  const rec = (k, slice) => (slice?.[k]?.records) || [];

  const generalFees = useMemo(() => rec('serviceFeesList', coreState), [coreState]);
  const intlFees = useMemo(() => rec('internationalServiceFeesList', coreState), [coreState]);
  const usdFees = useMemo(() => rec('internationalUSDtoUSDServiceFeesList', coreState), [coreState]);
  const bankAccountTypes = useMemo(() => rec('bankAccountTypesList', coreState), [coreState]);
  const banks = useMemo(() => rec('banksList', coreState), [coreState]);
  const holidays = useMemo(() => rec('holidaysList', coreState), [coreState]);
  const tenantBankAccounts = useMemo(() => rec('tenantBankAccountsList', coreState), [coreState]);
  const paymentFrequencies = useMemo(() => rec('paymentFrequenciesList', coreState), [coreState]);
  const services = useMemo(() => rec('servicesList', subsState), [subsState]);

  // ---- the cross-service join, same rule as the Billing Explorer ----
  const pricedIds = useMemo(() => new Set([
    ...generalFees, ...intlFees, ...usdFees,
  ].map((f) => String(f.ref_service_id ?? "").trim())), [generalFees, intlFees, usdFees]);

  const knownServiceIds = useMemo(() => new Set(services.map((s) => String(s.id))), [services]);

  const unpricedServices = services.filter((s) => !pricedIds.has(String(s.id)));
  const orphanFees = [...generalFees, ...intlFees, ...usdFees]
    .filter((f) => !knownServiceIds.has(String(f.ref_service_id ?? "").trim()));

  const intlLadders = useMemo(() => countLadderIssues(intlFees), [intlFees]);
  const usdLadders = useMemo(() => countLadderIssues(usdFees), [usdFees]);
  const brokenLadders = intlLadders.bad + usdLadders.bad;

  const inactiveFees = [...generalFees, ...intlFees, ...usdFees].filter((f) => f.is_active === false);
  const defaultBankAccounts = tenantBankAccounts.filter((a) => a.is_default);

  const attention = [
    {
      count: unpricedServices.length,
      one: "service in the catalogue has no fee defined for this tenant",
      many: "services in the catalogue have no fee defined for this tenant",
      to: "/business/core/tenants/billing-explorer",
    },
    {
      count: orphanFees.length,
      one: "fee row points at a service id that does not exist in identity",
      many: "fee rows point at a service id that does not exist in identity",
      to: "/business/core/tenants/billing-explorer",
    },
    {
      count: brokenLadders,
      one: "fee ladder has a gap or overlap in its amount bands",
      many: "fee ladders have gaps or overlaps in their amount bands",
      to: "/business/core/tenants/fee-ranges",
    },
    {
      count: tenantBankAccounts.length > 0 && defaultBankAccounts.length === 0 ? 1 : 0,
      one: "tenant has bank accounts but none is marked default",
      many: "tenant has bank accounts but none is marked default",
      to: "/business/core/tenants/tenant-bank-accounts",
    },
    {
      count: defaultBankAccounts.length > 1 ? defaultBankAccounts.length : 0,
      one: "bank account is marked default",
      many: "bank accounts are marked default at once",
      to: "/business/core/tenants/tenant-bank-accounts",
    },
  ]
    .filter((i) => Number(i.count) > 0)
    .map((i) => ({ ...i, label: Number(i.count) === 1 ? i.one : i.many }));

  const billingTiles = [
    { label: "General fees", value: generalFees.length, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/core/tenants/service-fees") },
    { label: "International bands", value: intlFees.length, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/core/tenants/international-service-fees") },
    { label: "USD → USD bands", value: usdFees.length, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/core/tenants/international-usd-to-usd-service-fees") },
    { label: "Fee ladders", value: intlLadders.lanes + usdLadders.lanes, accent: TILE_ACCENT.info, onClick: () => navigate("/business/core/tenants/fee-ranges") },
    { label: "Inactive fee rows", value: inactiveFees.length, accent: inactiveFees.length ? TILE_ACCENT.warn : TILE_ACCENT.good, onClick: () => navigate("/business/core/tenants/service-fees") },
  ];

  const coverageTiles = [
    { label: "Services in catalogue", value: services.length, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/core/tenants/billing-explorer") },
    { label: "Priced here", value: services.length - unpricedServices.length, accent: TILE_ACCENT.good, onClick: () => navigate("/business/core/tenants/billing-explorer") },
    { label: "No fee defined", value: unpricedServices.length, accent: unpricedServices.length ? TILE_ACCENT.bad : TILE_ACCENT.good, onClick: () => navigate("/business/core/tenants/billing-explorer") },
    { label: "Orphan fee rows", value: orphanFees.length, accent: orphanFees.length ? TILE_ACCENT.bad : TILE_ACCENT.good, onClick: () => navigate("/business/core/tenants/billing-explorer") },
  ];

  const accountTiles = [
    { label: "Bank account types", value: bankAccountTypes.length, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/core/tenants/bank-account-types") },
    { label: "Banks", value: banks.length, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/core/tenants/banks") },
    { label: "Tenant bank accounts", value: tenantBankAccounts.length, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/core/tenants/tenant-bank-accounts") },
    { label: "Holidays", value: holidays.length, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/core/tenants/holidays") },
    { label: "Payment frequencies", value: paymentFrequencies.length, accent: TILE_ACCENT.neutral, onClick: () => navigate("/business/core/tenants/payment-frequencies") },
  ];

  return (
    <Box>
      <TopBar
        title={selectedTenant?.tenant_name ? `Tenant — ${selectedTenant.tenant_name}` : "Tenant overview"}
        button1Text={"Refresh"}
        button1OnClick={() => handleGetAll()}
        />

      <Typography
        variant='caption'
        sx={{ color: LABEL_COLOR, display: "block", mb: "20px" }}>
        Everything on this screen is scoped to the selected tenant. Billing
        coverage is resolved against the identity service catalogue. Read-only.
      </Typography>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
        <Box>
          <Section title={"Needs attention"}>
            {
              attention.length === 0
              ?
              <Box
                className="flexCenter_Row"
                gap={"10px"}
                sx={{
                  border: `1px solid ${TILE_ACCENT.good}40`,
                  background: `${TILE_ACCENT.good}14`,
                  borderRadius: "12px",
                  padding: "14px 16px",
                }}>
                <CheckCircleOutlineOutlinedIcon sx={{ color: TILE_ACCENT.good }} />
                <Typography variant='body2'>
                  Nothing to action — every service is priced, every fee row
                  resolves, and every ladder is contiguous.
                </Typography>
              </Box>
              :
              <Box display={"flex"} flexDirection={"column"} gap={"8px"}>
                {
                  attention.map((item, index) => (
                    <Box
                      key={index}
                      className="flexCenterSBRow"
                      onClick={() => navigate(item.to)}
                      sx={{
                        border: `1px solid ${TILE_ACCENT.warn}40`,
                        background: `${TILE_ACCENT.warn}14`,
                        borderRadius: "12px",
                        padding: "12px 16px",
                        cursor: "pointer",
                        transition: "all .15s",
                        "&:hover": { background: `${TILE_ACCENT.warn}24`, borderColor: `${TILE_ACCENT.warn}80` },
                      }}>
                      <Box className="flexCenter_Row" gap={"12px"}>
                        <ReportProblemOutlinedIcon sx={{ color: TILE_ACCENT.warn }} />
                        <Typography variant='body2'>
                          <b>{item.count}</b>&nbsp;{item.label}
                        </Typography>
                      </Box>
                      <ChevronRightIcon sx={{ color: LABEL_COLOR }} />
                    </Box>
                  ))
                }
              </Box>
            }
          </Section>

          <Section
            title={"Billing coverage"}
            hint={"Identity owns the service catalogue; this tenant owns the pricing. These two are linked by service id across separate databases, so they can drift."}>
            <SummaryTiles tiles={coverageTiles} />
          </Section>

          <Section
            title={"Pricing tables"}
            hint={"A ladder is one service id and one lane (e.g. FX.Inbound) with its amount bands."}>
            <SummaryTiles tiles={billingTiles} />
          </Section>

          <Section title={"Account setup"}>
            <SummaryTiles tiles={accountTiles} />
          </Section>
        </Box>
      }
    </Box>
  )
}

export default HomePage;
